import React, { useEffect, useRef } from 'react';

const Cycle = () => {
  const rollerRef = useRef(null);

  useEffect(() => {
    const roller = rollerRef.current;
    if (!roller) return;

    // Clone the content to create a seamless loop
    const content = roller.innerHTML;
    roller.innerHTML = content + content;

    // Set up the animation
    const animate = () => {
      if (roller.scrollTop >= roller.scrollHeight / 2) {
        roller.scrollTop = 0;
      } else {
        roller.scrollTop += 1;
      }
    };

    const intervalId = setInterval(animate, 25);

    // Clean up on unmount
    return () => clearInterval(intervalId);
  }, []);

  return (
    <section className='py-20 bg-primary text-white'>
      <div className='container mx-auto px-4 max-w-6xl'>
        <div className='text-center mb-16'>
          <h2 className='text-3xl md:text-4xl font-bold mb-4'>
            The Cycle of Success
          </h2>
          <p className='text-lg md:text-xl text-gray-300 max-w-2xl mx-auto'>
            A continuous journey of growth and achievement
          </p>
        </div>

        <div className='relative flex justify-center items-center mb-24'>
          {/* Main Cycle Circle */}
          <div className='relative w-96 h-96 md:w-[32rem] md:h-[32rem]'>
            {/* Outer Circle */}
            <div className='absolute inset-0 rounded-full border-4 border-tertiary'></div>

            {/* Process Steps in Perfect Triangle */}
            <div className='absolute inset-0 uppercase'>
              {/* Step 1: Small Win - Top Left */}
              <div className='absolute top-[12.5%] left-[12.5%] transform -translate-x-1/2 -translate-y-1/2'>
                <div className='border-4 border-blue-800 bg-black rounded-full w-28 h-28 flex items-center justify-center text-center p-2 shadow-lg'>
                  <span className='text-md font-bold'>Small Win</span>
                </div>
              </div>

              {/* Step 2: Momentum - Top Right */}
              <div className='absolute top-[12.5%] right-[12.5%] transform translate-x-1/2 -translate-y-1/2'>
                <div className='border-4 border-purple-800 bg-black rounded-full w-28 h-28 flex items-center justify-center text-center p-2 shadow-lg'>
                  <span className='text-md font-bold'>Momentum</span>
                </div>
              </div>

              {/* Step 3: Habit - Bottom */}
              <div className='absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2'>
                <div className='border-4 border-green-800 bg-black rounded-full w-28 h-28 flex items-center justify-center text-center p-2 shadow-lg'>
                  <span className='text-md font-bold'>Habit</span>
                </div>
              </div>
            </div>

            {/* Keywords in the Center with Scroll Roller */}
            <div className='absolute inset-0 flex items-center justify-center'>
              <div className=' rounded-full w-64 h-64 md:w-72 md:h-72 flex flex-col items-center justify-center p-4 shadow-xl overflow-hidden'>
                {/* Keyword Roller */}
                <div
                  ref={rollerRef}
                  className='h-40 overflow-hidden w-full'
                  style={{
                    maskImage:
                      'linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)',
                    WebkitMaskImage:
                      'linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)',
                  }}
                >
                  <div className='flex flex-col items-center justify-center text-center'>
                    <div className='text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-green-500 py-1'>
                      RESULT
                    </div>
                    <div className='text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-blue-500 py-1'>
                      PROGRESS
                    </div>
                    <div className='text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500 py-1'>
                      TRANSFORMATION
                    </div>
                    <div className='text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-teal-500 py-1'>
                      HEALTHY LIFE
                    </div>
                    <div className='text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-500 py-1'>
                      STRONG MINDSET
                    </div>
                    <div className='text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-500 py-1'>
                      MOTIVATION
                    </div>
                    <div className='text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500 py-1'>
                      CONFIDENCE
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cycle of Success Description */}
        <div className='max-w-3xl mx-auto'>
          <div className='bg-secondary rounded-xl p-6 md:p-8 shadow-lg border border-tertiary'>
            <div className='space-y-6'>
              <div className='border-l-4 border-blue-700 pl-4'>
                <h3 className='text-xl font-bold text-white mb-2'>
                  Start Small, Think Big
                </h3>
                <p className='text-text-submain leading-relaxed'>
                  We believe real transformation starts with small wins. Every
                  time you show up — whether it's a quick workout, logging a
                  meal, or completing a daily challenge — you're building
                  momentum.
                </p>
              </div>

              <div className='border-l-4 border-purple-700 pl-4'>
                <h3 className='text-xl font-bold text-white mb-2'>
                  Build Unstoppable Momentum
                </h3>
                <p className='text-text-submain leading-relaxed'>
                  And once momentum kicks in, everything starts to shift.
                  Staying on track feels easier. Showing up becomes part of your
                  routine. And falling back into old habits? That gets harder
                  and harder. The more progress you make, the less you want to
                  look back.
                </p>
              </div>

              <div className='border-l-4 border-green-700 pl-4'>
                <h3 className='text-xl font-bold text-white mb-2'>
                  Create Lasting Change
                </h3>
                <p className='text-text-submain leading-relaxed'>
                  That momentum turns into habit. And habit leads to real,
                  lasting results.
                </p>
              </div>

              <div className='bg-tertiary rounded-lg p-4 mt-6'>
                <p className='text-lg font-medium text-white italic'>
                  "So if you ever feel stuck, don't try to do everything at
                  once. Just focus on your next small win. Show up. Build
                  momentum. The rest will follow."
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Cycle;
